/**
 * Pending actions — the dual-approval queue. Sensitive actions (notice
 * takedowns, account deactivations, amenity removals) wait here for a second
 * pair of eyes: any Approve-bit holder on the feature who ISN'T the requester.
 * Lives OUTSIDE /admin on purpose — a Chairman-position holder with a
 * RESIDENT persona must be able to approve. Master admin can override with a
 * mandatory recorded reason (bye-law 139's emergency-power discipline).
 * Wired: GET /api/positions/approvals, POST /api/positions/approvals/<id>,
 * POST /api/positions/approvals/<id>/override.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, Chip, EmptyState, Field, Input, Pill, Screen,
  SectionLabel, Sheet, Skeleton, TopBar,
} from '@/ui';

type Pending = {
  id: number; featureKey: string; action: string; summary: string;
  status: 'pending' | 'executed' | 'rejected' | 'expired';
  requestedBy: number; requestedByName: string | null;
  approvals: { userId: number; name: string; at: string }[];
  decidedByName: string | null; decisionReason: string;
  canApprove: boolean; createdAt: string;
};

const STATUS_PILL: Record<Pending['status'], { s: 'pending' | 'approved' | 'rejected'; l: string }> = {
  pending: { s: 'pending', l: 'WAITING' },
  executed: { s: 'approved', l: 'DONE' },
  rejected: { s: 'rejected', l: 'REJECTED' },
  expired: { s: 'rejected', l: 'EXPIRED' },
};

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    + ', ' + d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
}

export default function PendingActionsScreen() {
  const { colors } = useTheme();
  const { user } = useSession();
  const isMaster = user?.role === 'MASTER_ADMIN';

  const [tab, setTab] = React.useState<'pending' | 'all'>('pending');
  const [items, setItems] = React.useState<Pending[]>([]);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error' | 'forbidden'>('loading');
  const [busyId, setBusyId] = React.useState<number | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [overrideFor, setOverrideFor] = React.useState<Pending | null>(null);
  const [overrideReason, setOverrideReason] = React.useState('');

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const body = await api.get<{ items: Pending[] }>(`/api/positions/approvals?status=${tab}`);
      setItems(body.items ?? []);
      setState('ready');
    } catch (e) {
      setState((e as { status?: number })?.status === 403 ? 'forbidden' : 'error');
    }
  }, [tab]);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const decide = (row: Pending, decision: 'approve' | 'reject') => {
    setBusyId(row.id);
    setNotice(null);
    api.post<{ pending: Pending }>(`/api/positions/approvals/${row.id}`, { decision })
      .then((body) => {
        setItems((s) => s.map((i) => (i.id === row.id ? { ...i, ...body.pending, canApprove: false } : i)));
        setNotice(decision === 'approve'
          ? (body.pending.status === 'executed' ? 'Approved — the action has been carried out.' : 'Approval recorded.')
          : 'Rejected — nothing was changed.');
      })
      .catch((e) => setNotice((e as { error?: string })?.error ?? 'Could not record your decision.'))
      .finally(() => setBusyId(null));
  };

  const doOverride = () => {
    if (!overrideFor) return;
    if (overrideReason.trim().length < 5) {
      setNotice('An override needs a written reason — it goes on the audit trail.');
      return;
    }
    const row = overrideFor;
    setBusyId(row.id);
    api.post<{ pending: Pending }>(`/api/positions/approvals/${row.id}/override`, { reason: overrideReason.trim() })
      .then((body) => {
        setItems((s) => s.map((i) => (i.id === row.id ? { ...i, ...body.pending, canApprove: false } : i)));
        setNotice('Overridden — action carried out and the reason recorded.');
      })
      .catch((e) => setNotice((e as { error?: string })?.error ?? 'Could not override.'))
      .finally(() => { setBusyId(null); setOverrideFor(null); setOverrideReason(''); });
  };

  const visible = tab === 'pending' ? items.filter((i) => i.status === 'pending') : items;

  return (
    <Screen scroll={false}>
      <TopBar title="Pending actions" onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s3 }}>
        <AppText variant="bodySm" tone="tertiary">
          Sensitive actions wait for a second approval before they happen —
          the person who raised one can never approve it themselves.
        </AppText>
        <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s3, marginBottom: space.s3 }}>
          <Chip label="Waiting" active={tab === 'pending'} onPress={() => setTab('pending')} />
          <Chip label="History" active={tab === 'all'} onPress={() => setTab('all')} />
        </View>

        {notice ? (
          <AppText variant="bodySm" weight="500" style={{ marginBottom: space.s3, color: colors.primary700 }}>
            {notice}
          </AppText>
        ) : null}

        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={96} />)}</View>
        ) : state === 'forbidden' ? (
          <EmptyState
            icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
            title="Approvers only"
            body="This queue is for committee positions that hold an Approve permission."
          />
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
            title="Couldn't load the queue"
            body="Check your connection and try again."
            cta="Retry"
            onCta={() => { void load(); }}
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<Icon name="check" size={40} color={colors.textTertiary} />}
            title={tab === 'pending' ? 'Nothing waiting' : 'No history yet'}
            body={tab === 'pending' ? 'Sensitive actions that need a second approval will appear here.' : undefined}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {visible.map((row) => {
              const pill = STATUS_PILL[row.status];
              return (
                <Card key={row.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s2 }}>
                    <AppText variant="bodyMd" weight="600" style={{ flex: 1 }}>{row.summary}</AppText>
                    <Pill status={pill.s} label={pill.l} />
                  </View>
                  <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>
                    Raised by {row.requestedByName ?? 'a member'} · {fmtWhen(row.createdAt)}
                    {row.approvals.length > 0 ? ` · approved by ${row.approvals.map((a) => a.name).join(', ')}` : ''}
                  </AppText>
                  {row.status !== 'pending' && (row.decidedByName || row.decisionReason) ? (
                    <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                      {row.decidedByName ? `Decided by ${row.decidedByName}` : ''}
                      {row.decisionReason ? `${row.decidedByName ? ' — ' : ''}"${row.decisionReason}"` : ''}
                    </AppText>
                  ) : null}
                  {row.status === 'pending' ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s3 }}>
                      {row.canApprove ? (
                        <>
                          <SmallButton label="Approve" icon="check" disabled={busyId === row.id}
                            onPress={() => decide(row, 'approve')} />
                          <SmallButton label="Reject" danger disabled={busyId === row.id}
                            onPress={() => decide(row, 'reject')} />
                        </>
                      ) : (
                        <AppText variant="bodySm" tone="tertiary" style={{ flex: 1 }}>
                          {row.requestedBy && user && String(row.requestedBy) === String(user.id)
                            ? 'You raised this — someone else must decide.'
                            : 'Waiting for an approver.'}
                        </AppText>
                      )}
                      <View style={{ flex: 1 }} />
                      {isMaster ? (
                        <SmallButton label="Override" icon="key" disabled={busyId === row.id}
                          onPress={() => { setOverrideFor(row); setOverrideReason(''); }} />
                      ) : null}
                    </View>
                  ) : null}
                </Card>
              );
            })}
          </View>
        )}
      </Screen>

      <Sheet visible={overrideFor !== null} onClose={() => setOverrideFor(null)}>
        <SectionLabel>Override without second approval</SectionLabel>
        <AppText variant="bodySm" tone="secondary" style={{ marginTop: space.s2, marginBottom: space.s3 }}>
          Emergency powers come with paperwork: the reason is recorded on the
          society&apos;s audit trail with your name.
        </AppText>
        <Field label="Reason">
          <Input value={overrideReason} onChangeText={setOverrideReason}
            placeholder="Why this can't wait for a second approver" maxLength={250} multiline />
        </Field>
        <Button title="Record reason & execute" style={{ marginTop: space.s4 }} onPress={doOverride} />
      </Sheet>
    </Screen>
  );
}
